import React, { useEffect, useState } from 'react';
import Button from '../common/Button';
import { Check, X } from 'lucide-react';
import InternetProblem from '../../pages/InternetProblem';
import { updateUserScores } from '../../services/leaderboardService';
import { useAuth } from '../../contexts/AuthContext';
import { incrementChallengeScore } from '../../services/challengeService';
import { logUserActivity } from '../../services/activityLogService';

interface FillBlankActivityProps {
  onComplete: () => void;
  setFillBlankActivity: (value: boolean) => void;
  setTrueFalseActivity: (value: boolean) => void;
  getFillActivity: (activity: string, word: object) => Promise<boolean>;
  translated: string;
  currentWord: object | undefined | null;
  saveDayExercise: (activity: string) => void;
  getDayExercise: (activity: string, word: object) => Promise<boolean>;
  setShowReward: (value: boolean) => void;
  setPoints: (value: number) => void;
  onPointsEarned: (points: number) => void;
  phrase: string;
  word: string | object | undefined | null;
}

const FillBlankActivity: React.FC<FillBlankActivityProps> = ({ setPoints, onPointsEarned, phrase, word, onComplete, setFillBlankActivity, setTrueFalseActivity, getFillActivity, translated, currentWord, saveDayExercise, getDayExercise, setShowReward }) => {
  const [userAnswer, setUserAnswer] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const { currentUser } = useAuth();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (hasSubmitted) return;
    setUserAnswer(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !hasSubmitted && userAnswer.trim()) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const checkExercise = async () => {
    const isCompleted = await getDayExercise('fillBlank', currentWord);
    if (isCompleted) {
      setHasSubmitted(true);
      setIsCorrect(true);
    } else {
      setHasSubmitted(false);
      setIsCorrect(false);
    }
  }

  const handleSubmit = () => {
    if (!userAnswer || hasSubmitted) return;
    
    // Case insensitive comparison
    const correct = word.toLowerCase().includes(userAnswer.toLowerCase());
    setIsCorrect(correct);
    setHasSubmitted(true);
    saveDayExercise('fillBlank');
    
    if (correct) {
      let earned = 25;
      if (failedAttempts === 0) earned = 100;
      else if (failedAttempts === 1) earned = 50;
      onPointsEarned(earned);
      setPoints(earned);
      setShowReward(true);
      
      setTimeout(() => {
        onComplete();
        setShowReward(false);
      }, 1500);
    } else {
      setFailedAttempts(prev => prev + 1);
    }
  };

  const handleReset = () => {
    setUserAnswer('');
    setHasSubmitted(false);
    setIsCorrect(false);
    getFillActivity();
  };

  useEffect(() => {
    checkExercise();
  }, []);

  // Create the sentence with blank
    const sentenceParts = phrase.replace(/{\/?it}|\/it\//g, '').replace(/[\[\]=;]/g, '').split('_____');

  if(!phrase) {
    console.log(phrase)
    return <InternetProblem />
  }

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 p-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">Fill in the Blank</h3>
      
      <div className="mb-6">
        <p className="text-lg text-gray-700">
          {sentenceParts[0]}
          <span className={`inline-block min-w-20 px-2 mx-1 border-b-2 ${
            hasSubmitted
              ? isCorrect
                ? 'border-green-500 bg-green-50'
                : 'border-red-500 bg-red-50'
              : 'border-gray-400'
          }`}>
            <input
              type="text"
              value={userAnswer}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={hasSubmitted}
              className={`w-full bg-transparent focus:outline-none ${
                hasSubmitted 
                  ? isCorrect 
                    ? 'text-green-700 font-medium' 
                    : 'text-red-700 font-medium'
                  : 'text-gray-800'
              }`}
              placeholder="Type word (Press Enter to submit)"
            />
          </span>
          {sentenceParts[1]}
        </p>
        <p className="text-sm text-gray-700 italic">{translated}</p>
      </div>
      
      {hasSubmitted && (
        <div className={`p-4 rounded-lg mb-4 ${isCorrect ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          <div className="flex items-center">
            {isCorrect ? (
              <Check className="h-5 w-5 mr-2" />
            ) : (
              <X className="h-5 w-5 mr-2" />
            )}
            <p className="font-medium">
              {isCorrect 
                ? 'Correct! Great job!' 
                : `Incorrect. The correct answer is "${(word as string)?.replace(/{\/?it}|\/it\//g, '')}".`}
            </p>
          </div>
        </div>
      )}
      
      <div className="flex justify-end space-x-3">
        {hasSubmitted && !isCorrect ? (
          <Button variant="outline" onClick={handleReset}>
            Try Again
          </Button>
        ) : hasSubmitted && isCorrect ? (
          <Button onClick={() => onComplete()}>
            Next Activity
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit} 
            disabled={!userAnswer.trim() || hasSubmitted}
          >
            {hasSubmitted ? 'Completed' : 'Check Answer'}
          </Button>
        )}
        {hasSubmitted && !isCorrect && (
          <Button variant="outline" onClick={() => {
            onPointsEarned(0);
            setFillBlankActivity(false);
            setTrueFalseActivity(true);
          }}>
            Skip
          </Button>
        )}
      </div>
    </div>
  );
};

export default FillBlankActivity;